import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, useTheme } from '@ublend-npm/aulaui-core';
import { useDispatch, useSelector } from 'react-redux';
import { fetchHomeSpaces } from '@app/actions/home/fetchHomeSpaces';
import { fetchRecentlyViewedSpaces } from '@core/spaceSelector/operations';
import useBreakpoints from '@app/hooks/useBreakpoints';
import {
  getHomeSpaceCardsForView,
  getHomeSpacesError,
  getHomeSpacesErrorType,
  getHomeSpacesHasMore,
  getHomeSpacesLoading,
  getHomeViewRole,
} from '@app/selectors/home';
import HomeGreeting from '../greeting';
import SpaceWidgetCarousel from './SpaceWidgetCarousel';
import SpaceWidgetEmptyState from './SpaceWidgetEmptyState';
import SpaceWidgetToolbar from './SpaceWidgetToolbar';
import SpaceWidgetSkeleton from './SpaceWidgetSkeleton';
import { getSpaceWidgetColors } from './spaceWidgetColors';
import {
  SPACE_WIDGET_CONTAINER_BORDER_RADIUS,
  SPACE_WIDGET_CONTAINER_GAP,
  SPACE_WIDGET_CONTAINER_MIN_HEIGHT,
  SPACE_WIDGET_CONTAINER_PADDING,
  SPACE_WIDGET_PREFETCH_MIN_VISIBLE_CARDS_LG,
  SPACE_WIDGET_PREFETCH_MIN_VISIBLE_CARDS_SM,
  SPACE_WIDGET_PAGINATION_ERROR_THRESHOLD,
  SPACE_WIDGET_SCROLL_PREFETCH_REMAINING_SLIDES,
  SPACE_WIDGET_SKELETON_CARD_COUNT_LG,
  SPACE_WIDGET_SKELETON_CARD_COUNT_SM,
} from './spaceWidgetConstants';
import {
  SpaceWidgetCardData,
  SpaceWidgetSectionProps,
} from './SpaceWidget.types';
import { useSpaceWidgetSwiper } from './useSpaceWidgetSwiper';

type SpaceWidgetSectionPropsWithDefaults = SpaceWidgetSectionProps & {
  spaces?: SpaceWidgetCardData[];
};

const SpaceWidgetSection = ({
  showRoleToggle = true,
  showGreeting = true,
  showSpaceCarousel = true,
  spaces: spacesOverride,
}: SpaceWidgetSectionPropsWithDefaults) => {
  const dispatch = useDispatch();
  const { palette } = useTheme();
  const widgetColors = getSpaceWidgetColors(palette);
  const { lg: isLgUp } = useBreakpoints();
  const viewRole = useSelector(getHomeViewRole);
  const spacesFromStore = useSelector(getHomeSpaceCardsForView);
  const spacesLoading = useSelector(getHomeSpacesLoading);
  const spacesHasMore = useSelector(getHomeSpacesHasMore);
  const spacesError = useSelector(getHomeSpacesError);
  const spacesErrorType = useSelector(getHomeSpacesErrorType);
  const [initialSpacesFetchSettled, setInitialSpacesFetchSettled] = useState(
    Boolean(spacesOverride)
  );
  const hasRequestedSpacesRef = useRef(Boolean(spacesOverride));
  const wasSpacesLoadingRef = useRef(false);
  const visibleSpaceCountAtFetchStartRef = useRef(0);
  const [paginationFailureCount, setPaginationFailureCount] = useState(0);
  const visibleSpaces = useMemo(() => {
    if (spacesOverride) {
      return spacesOverride.filter((space) => space.role === viewRole);
    }

    return spacesFromStore;
  }, [spacesFromStore, spacesOverride, viewRole]);
  const skeletonCardCount = isLgUp
    ? SPACE_WIDGET_SKELETON_CARD_COUNT_LG
    : SPACE_WIDGET_SKELETON_CARD_COUNT_SM;
  const previouslyFetchedWithNoMore =
    !spacesOverride && !spacesLoading && !spacesHasMore;
  const showSpacesSkeleton =
    !spacesOverride &&
    !spacesError &&
    spacesLoading &&
    visibleSpaces.length === 0;
  const spacesSettled = spacesOverride
    ? true
    : previouslyFetchedWithNoMore ||
      spacesError ||
      (!spacesLoading && initialSpacesFetchSettled);
  const showErrorState =
    !spacesOverride &&
    showSpaceCarousel &&
    spacesError &&
    !spacesLoading &&
    visibleSpaces.length === 0;
  const showPaginationErrorCard =
    !spacesOverride &&
    showSpaceCarousel &&
    visibleSpaces.length > 0 &&
    spacesHasMore &&
    !spacesLoading &&
    paginationFailureCount >= SPACE_WIDGET_PAGINATION_ERROR_THRESHOLD;
  const showCarouselErrorCard = showErrorState || showPaginationErrorCard;
  const showEmptyState =
    showSpaceCarousel &&
    !spacesError &&
    !spacesLoading &&
    spacesSettled &&
    visibleSpaces.length === 0;
  const showGreetingInCarousel = showGreeting && isLgUp && !showEmptyState;
  const showGreetingAboveSection = showGreeting && (!isLgUp || showEmptyState);
  const minVisibleCards = isLgUp
    ? SPACE_WIDGET_PREFETCH_MIN_VISIBLE_CARDS_LG
    : SPACE_WIDGET_PREFETCH_MIN_VISIBLE_CARDS_SM;
  const showTrailingSkeletons =
    !spacesOverride &&
    spacesLoading &&
    spacesHasMore &&
    visibleSpaces.length > 0 &&
    !showPaginationErrorCard &&
    !showErrorState;
  const trailingSkeletonCount = showTrailingSkeletons
    ? Math.max(1, minVisibleCards - visibleSpaces.length)
    : 0;
  const carouselItemCount =
    (showGreetingInCarousel ? 1 : 0) +
    (showSpaceCarousel ? visibleSpaces.length : 0) +
    trailingSkeletonCount +
    (showCarouselErrorCard ? 1 : 0);

  const loadMoreSpaces = () => {
    if (spacesOverride || showPaginationErrorCard || showErrorState) {
      return;
    }

    dispatch(fetchHomeSpaces() as any);
  };

  const handleRetrySpaces = () => {
    if (spacesOverride) {
      return;
    }

    dispatch(fetchHomeSpaces() as any);
  };

  const shouldAutoPrefetch =
    !spacesOverride &&
    !spacesLoading &&
    !spacesError &&
    spacesHasMore &&
    visibleSpaces.length < minVisibleCards;

  const {
    handleSwiper,
    handleSlideChange,
    handleReachEnd,
    handleTransitionEnd,
    canScrollBack,
    canScrollForward,
    handleScroll,
  } = useSpaceWidgetSwiper({
    itemCount: carouselItemCount,
    hasMore: spacesOverride ? false : spacesHasMore,
    loading: spacesOverride ? false : spacesLoading,
    onNearEnd: loadMoreSpaces,
    resetKey: viewRole,
    prefetchRemainingSlides: SPACE_WIDGET_SCROLL_PREFETCH_REMAINING_SLIDES,
  });

  useEffect(() => {
    if (spacesLoading) {
      hasRequestedSpacesRef.current = true;

      if (!wasSpacesLoadingRef.current) {
        visibleSpaceCountAtFetchStartRef.current = visibleSpaces.length;
      }
    }

    if (
      wasSpacesLoadingRef.current &&
      !spacesLoading &&
      !spacesOverride &&
      spacesError &&
      visibleSpaces.length > 0 &&
      visibleSpaces.length === visibleSpaceCountAtFetchStartRef.current
    ) {
      setPaginationFailureCount((count) => count + 1);
    }

    if (
      wasSpacesLoadingRef.current &&
      !spacesLoading &&
      !spacesError &&
      visibleSpaces.length > visibleSpaceCountAtFetchStartRef.current
    ) {
      setPaginationFailureCount(0);
    }

    wasSpacesLoadingRef.current = spacesLoading;
  }, [spacesLoading, spacesError, spacesOverride, visibleSpaces.length]);

  useEffect(() => {
    setPaginationFailureCount(0);
  }, [viewRole]);

  useEffect(() => {
    if (spacesOverride) {
      return;
    }

    if (spacesError) {
      setInitialSpacesFetchSettled(true);
      return;
    }

    if (!spacesLoading && !spacesHasMore) {
      setInitialSpacesFetchSettled(true);
      return;
    }

    if (!spacesLoading && hasRequestedSpacesRef.current) {
      setInitialSpacesFetchSettled(true);
    }
  }, [spacesOverride, spacesLoading, spacesError, spacesHasMore]);

  useEffect(() => {
    if (!shouldAutoPrefetch) {
      return;
    }

    dispatch(fetchHomeSpaces() as any);
  }, [dispatch, shouldAutoPrefetch]);

  useEffect(() => {
    if (spacesOverride) {
      return;
    }

    dispatch(fetchRecentlyViewedSpaces() as any);
  }, [dispatch, spacesOverride]);

  const showToolbar = showRoleToggle || showSpaceCarousel;
  const showCarouselRow = showGreetingInCarousel || showSpaceCarousel;

  if (!showToolbar && !showCarouselRow && !showGreetingAboveSection) {
    return null;
  }

  return (
    <Box
      width="100%"
      sx={{
        minWidth: 0,
        maxWidth: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
      }}
    >
      {showGreetingAboveSection ? (
        <Box
          id="home-greeting-widget"
          aria-label="Greeting Widgets"
          sx={{
            width: '100%',
            minWidth: 0,
            maxWidth: '100%',
            mb: SPACE_WIDGET_CONTAINER_GAP,
          }}
        >
          <HomeGreeting compact={isLgUp && showEmptyState} />
        </Box>
      ) : null}

      {!showToolbar && !showCarouselRow ? null : (
        <Box
          width="100%"
          sx={{
            minWidth: 0,
            maxWidth: '100%',
            overflow: 'hidden',
            minHeight: showEmptyState
              ? undefined
              : { lg: SPACE_WIDGET_CONTAINER_MIN_HEIGHT },
            borderRadius: SPACE_WIDGET_CONTAINER_BORDER_RADIUS,
            border: widgetColors.containerBorder,
            bgcolor: widgetColors.containerBg,
            p: SPACE_WIDGET_CONTAINER_PADDING,
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            gap: SPACE_WIDGET_CONTAINER_GAP,
          }}
        >
          {showToolbar ? (
            <SpaceWidgetToolbar
              canScrollBack={canScrollBack}
              canScrollForward={canScrollForward}
              onScrollBack={() => handleScroll('back')}
              onScrollForward={() => handleScroll('forward')}
              showNavigation={
                (showGreetingInCarousel || showSpaceCarousel) && !showEmptyState
              }
              showRoleToggle={showRoleToggle}
            />
          ) : null}

          {showCarouselRow ? (
            <Box
              id="home-space-widget"
              aria-label="Space Widget"
              sx={{
                minWidth: 0,
                maxWidth: '100%',
                width: '100%',
                overflow: 'hidden',
              }}
            >
              {showSpacesSkeleton ? (
                <SpaceWidgetSkeleton
                  cardCount={skeletonCardCount}
                  showGreeting={showGreetingInCarousel}
                />
              ) : showEmptyState ? (
                <SpaceWidgetEmptyState />
              ) : (
                <SpaceWidgetCarousel
                  spaces={showSpaceCarousel ? visibleSpaces : []}
                  showGreeting={showGreetingInCarousel}
                  showErrorCard={showCarouselErrorCard}
                  errorVariant={spacesErrorType ?? 'api'}
                  onErrorRetry={handleRetrySpaces}
                  errorRetrying={spacesLoading && showErrorState}
                  trailingSkeletonCount={trailingSkeletonCount}
                  onSwiper={handleSwiper}
                  onSlideChange={handleSlideChange}
                  onReachEnd={handleReachEnd}
                  onTransitionEnd={handleTransitionEnd}
                />
              )}
            </Box>
          ) : null}
        </Box>
      )}
    </Box>
  );
};

export default SpaceWidgetSection;
